import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Chip,
} from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import { useAppSelector } from '@hooks/redux'

interface HiveSelectorProps {
  selectedHives: number[]
  onSelectionChange: (hiveIds: number[]) => void
  selectedApiaryId?: number | null
  onApiaryChange?: (apiaryId: number | null) => void
  disabled?: boolean
}

interface HiveIconProps {
  hive: any
  isSelected: boolean
  onClick: () => void
  disabled: boolean
}

const HiveIcon: React.FC<HiveIconProps> = ({ hive, isSelected, onClick, disabled }) => {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        mr: 1,
        mb: 1,
      }}
    >
      {/* Checkmark indicator */}
      {isSelected && (
        <Box sx={{ height: 24, mb: 0.25 }}>
          <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
        </Box>
      )}
      {!isSelected && <Box sx={{ height: 24, mb: 0.25 }} />}

      {/* Hive box representation */}
      <Paper
        elevation={isSelected ? 3 : 1}
        sx={{
          width: { xs: 56, sm: 48, md: 44 },
          height: { xs: 64, sm: 54, md: 50 },
          minWidth: 44,
          backgroundColor: hive.color || '#F8B133',
          border: isSelected ? '2px solid #4caf50' : '1px solid rgba(0,0,0,0.2)',
          borderRadius: '2px 2px 0 0',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          '&:hover': disabled ? {} : {
            transform: 'translateY(-2px)',
            boxShadow: 3,
          },
          // Add horizontal lines to simulate frames/layers
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '30%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'rgba(0,0,0,0.15)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '60%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'rgba(0,0,0,0.15)',
          },
        }}
      />

      {/* Hive name */}
      <Typography
        variant="caption"
        sx={{
          mt: 0.5,
          maxWidth: 88,
          textAlign: 'center',
          fontSize: { xs: '0.8rem', md: '0.7rem' },
          lineHeight: 1.1,
          color: '#666',
          wordBreak: 'break-word',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {hive.name}
      </Typography>
    </Box>
  )
}

const HiveSelector: React.FC<HiveSelectorProps> = ({
  selectedHives,
  onSelectionChange,
  selectedApiaryId,
  onApiaryChange,
  disabled = false,
}) => {
  const { apiaries, hives } = useAppSelector((state) => state.locations)
  const [selectAllHives, setSelectAllHives] = useState(false)

  const uniqueHives = useMemo(() => {
    const byId = new Map<number, (typeof hives)[number]>()
    hives.forEach((hive) => {
      if (!byId.has(hive.id)) {
        byId.set(hive.id, hive)
      }
    })
    return Array.from(byId.values())
  }, [hives])

  // Group hives by apiary (location)
  const groupedHives = useMemo(() => {
    const groups: Record<string, any[]> = {}

    uniqueHives.forEach(hive => {
      const locationId = hive.location_id || 'ungrouped'
      if (!groups[locationId]) {
        groups[locationId] = []
      }
      groups[locationId].push(hive)
    })

    return groups
  }, [uniqueHives])

  // Get current apiary's hives
  const currentApiaryHives = useMemo(() => {
    if (!selectedApiaryId) {
      return uniqueHives
    }
    return groupedHives[selectedApiaryId] || []
  }, [selectedApiaryId, groupedHives, uniqueHives])

  // Toggle individual hive
  const toggleHive = (hiveId: number) => {
    if (disabled) return

    if (selectedHives.includes(hiveId)) {
      onSelectionChange(selectedHives.filter(id => id !== hiveId))
      setSelectAllHives(false)
    } else {
      onSelectionChange([...selectedHives, hiveId])

      // Check if all hives are now selected
      const apiaryHiveIds = currentApiaryHives.map(h => h.id)
      if ([...selectedHives, hiveId].filter(id => apiaryHiveIds.includes(id)).length === apiaryHiveIds.length) {
        setSelectAllHives(true)
      }
    }
  }

  // Toggle all hives
  const toggleAllHives = () => {
    if (disabled) return

    if (selectAllHives) {
      // Deselect all hives from current apiary
      const apiaryHiveIds = currentApiaryHives.map(h => h.id)
      onSelectionChange(selectedHives.filter(id => !apiaryHiveIds.includes(id)))
      setSelectAllHives(false)
    } else {
      // Select all hives from current apiary
      const apiaryHiveIds = currentApiaryHives.map(h => h.id)
      const newSelection = [...new Set([...selectedHives, ...apiaryHiveIds])]
      onSelectionChange(newSelection)
      setSelectAllHives(true)
    }
  }

  // Get selected apiary name
  const selectedApiary = apiaries.find(a => a.id === selectedApiaryId)

  return (
    <Box>
      {/* Selected hives summary */}
      {selectedHives.length > 0 && (
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedHives.map(hiveId => {
            const hive = uniqueHives.find(h => h.id === hiveId)
            return hive ? (
              <Chip
                key={hiveId}
                label={hive.name}
                size="small"
                onDelete={disabled ? undefined : () => toggleHive(hiveId)}
                sx={{ backgroundColor: hive.color || '#F8B133', color: '#fff' }}
              />
            ) : null
          })}
        </Box>
      )}

      {/* Select all toggle */}
      {currentApiaryHives.length > 0 && (
        <FormControlLabel
          control={
            <Switch
              checked={selectAllHives}
              onChange={toggleAllHives}
              disabled={disabled}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              {selectedApiary ? `Select all hives in ${selectedApiary.name}` : 'Select all hives'}
            </Typography>
          }
          sx={{ mb: 1 }}
        />
      )}

      {/* Hive icons display */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          p: 2,
          backgroundColor: '#fafafa',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
          minHeight: 100,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
        }}
      >
        {currentApiaryHives.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hives available
          </Typography>
        ) : (
          currentApiaryHives.map((hive) => (
            <HiveIcon
              key={hive.id}
              hive={hive}
              isSelected={selectedHives.includes(hive.id)}
              onClick={() => toggleHive(hive.id)}
              disabled={disabled}
            />
          ))
        )}
      </Box>
    </Box>
  )
}

export default HiveSelector
